/**
 * A «dropped door» line, and the need it leaves out of every proposal (bata-platform ADR 6038, the
 * minimum size run).
 *
 * **What it is.** When a door's pairs for an article are fewer than the article's core sizes, no size
 * run can be composed, and the engine does not send a broken one. It writes the decision down as ONE
 * line for the store × article instead of letting the door vanish from the record: the
 * store-total size (`size_key` is the empty string, the platform's `STORE_TOTAL_SIZE_KEY`), `units` 0,
 * `bound_by` `size_run_dropped`, no size name. Its `requested_units` is, by design, the door's need
 * SUMMED over the article's sizes. Such a store × article never also has sized lines in the run, so
 * nothing is counted twice.
 *
 * **Why the screens set it apart.** Read as a line, it says «requested 5, proposed 0» beside a size
 * that is not a size, and a planner takes the 5 for a request the engine turned down, or for a size
 * that is missing. It is neither: it is a need the run could not turn into any shipment, and it is
 * the largest single block of unmet need on a production run (2026-10-13: 3,808 of 31,333 lines,
 * 23% of the requested pairs, none of them proposed). So every surface names the line in words,
 * calls its request «unservable need», and every export keeps it out of the table of what was
 * proposed and carries it on a sheet of its own.
 *
 * **One predicate.** The store-total marker already exists for the stock ledger
 * (`isStoreTotalPosition`, `docs/adr/6023`): a position at the store-total grain, whose size the
 * ledger never holds. The platform writes a line at that size for one reason only, the dropped door,
 * so the two are the same lines; this module reuses the marker rather than inventing a second.
 *
 * Pure: no React, no network.
 */

import type { ProposalSummary } from '@/api/generated';

import { isStoreTotalPosition } from './stock-summary';

/** The platform's store-total size key (`planning.allocation.engine.STORE_TOTAL_SIZE_KEY`). */
export const STORE_TOTAL_SIZE_KEY = '';

/** The `bound_by` code a dropped door's line carries. */
export const DROPPED_BOUND_BY = 'size_run_dropped';

/**
 * What a line publishes for the question to be asked: its size key, the grain of its position, and —
 * so a real proposal is never taken for a marker — the constraint that bound it and the pairs it proposes.
 */
export type DroppedDoorCandidate = Pick<ProposalSummary, 'size_key' | 'position_grain' | 'bound_by' | 'units'>;

/**
 * Whether the line is a dropped door's: at the store-total size (its key, or the grain the platform
 * marks it with; a platform older than the marker publishes the key alone) AND proposing nothing.
 *
 * **It never hides a real proposal.** A store-total line that carries proposed pairs is a proposal like
 * any other and stays where it was. So a caller that holds `bound_by` has it equal `size_run_dropped`,
 * and one that holds `units` has them at nought; a caller that holds fewer fields than a whole line
 * (a tally's, a roll-up's cell) asks with what it has. A line that names no store-total size is not one.
 */
export function isDroppedDoorLine(line: Partial<DroppedDoorCandidate>): boolean {
  if (line.size_key !== STORE_TOTAL_SIZE_KEY && !isStoreTotalPosition(line)) return false;
  if (line.bound_by !== undefined && line.bound_by !== DROPPED_BOUND_BY) return false;
  return line.units === undefined || line.units === 0;
}

/** The lines split into those that propose something and the dropped doors, each in its own order. */
export function splitDroppedDoors<T extends DroppedDoorCandidate>(
  lines: readonly T[],
): { readonly served: readonly T[]; readonly dropped: readonly T[] } {
  const served: T[] = [];
  const dropped: T[] = [];
  for (const line of lines) (isDroppedDoorLine(line) ? dropped : served).push(line);
  return { served, dropped };
}

/**
 * What the dropped doors of a group of lines leave out (bata-platform ADR 6183): the markers
 * (`lines` — one per store × article, so NOT the stores), the distinct stores whose lines are ALL
 * markers (`doors`), and the pairs those markers needed (`pairs`). A door's status is relative to the
 * group: dropped for one article and served for another, it is served in its store's group and in the
 * run's, and dropped in the first article's.
 */
export interface DroppedNeed {
  readonly pairs: number;
  readonly lines: number;
  readonly doors: number;
}

/** Nothing dropped. */
export const NO_DROPPED_NEED: DroppedNeed = { pairs: 0, lines: 0, doors: 0 };

/** The five fields every in-season read states beside its old counts (ADR 6183); each is absent on a platform that predates them. */
export interface DroppedFields {
  readonly dropped_door_lines?: number | undefined;
  readonly dropped_door_requested_units?: number | undefined;
  readonly dropped_doors?: number | undefined;
}

/** The platform's own statement of a group's dropped doors, or nothing where it states none or none are dropped. */
export function droppedNeedOf(fields: DroppedFields): DroppedNeed {
  const lines = fields.dropped_door_lines ?? 0;
  return lines <= 0 ? NO_DROPPED_NEED : { lines, pairs: fields.dropped_door_requested_units ?? 0, doors: fields.dropped_doors ?? 0 };
}

/**
 * The same, folded from lines a page holds WHOLE (a store's, a run's on the Why page): the markers, the
 * pairs they needed, and the stores with no line that is not a marker. For a page that holds a part of
 * the lines, read the platform's figure (`droppedNeedOf`) instead. Pure.
 */
export function droppedNeedOfLines(
  lines: readonly (DroppedDoorCandidate & Pick<ProposalSummary, 'requested_units' | 'store_key'>)[],
): DroppedNeed {
  let pairs = 0;
  let count = 0;
  const withMarker = new Set<string>();
  const withServed = new Set<string>();
  for (const line of lines) {
    if (isDroppedDoorLine(line)) {
      pairs += line.requested_units;
      count += 1;
      withMarker.add(line.store_key);
    } else {
      withServed.add(line.store_key);
    }
  }
  if (count === 0) return NO_DROPPED_NEED;
  return { pairs, lines: count, doors: [...withMarker].filter((store) => !withServed.has(store)).length };
}

/** {@link droppedNeedOf} over the platform's roll-up of a run (`proposals/by-door-size`). */
export function droppedNeedOfRollup(rollup: DroppedFields): DroppedNeed {
  return droppedNeedOf(rollup);
}

// ── the words ────────────────────────────────────────────────────────────────

/** The catalogue keys every surface says a dropped door in. Literal, so the catalogue check sees each. */
export const DROPPED_DOOR_KEY = {
  /** In a size column, where a size would be: «Totale negozio · serie di taglie non componibile». */
  size: 'inseason.droppedDoor.size',
  /** The line's request, said as what it is: «Fabbisogno non servibile (sotto la serie minima)». */
  need: 'inseason.droppedDoor.need',
  /** The same, with the pairs: a cell of a grid that carries one text per cell. */
  needValue: 'inseason.droppedDoor.needValue',
  /** On an aggregated row: «di cui {pairs} non servibili». */
  needPart: 'inseason.droppedDoor.needPart',
  /** On a count of a group's parts: «di cui {count} scartati». */
  countPart: 'inseason.droppedDoor.countPart',
  /** On a served count, the dropped ones being apart from it: «e altri {count} scartati». */
  countAlso: 'inseason.droppedDoor.countAlso',
} as const;

/**
 * The fields that tell a proposed line from a marker, taken from a side of a simulation's diff (the
 * scenario's, else the live run's): none where the diff holds no line on either side.
 */
export function droppedFieldsOf(
  side: Pick<ProposalSummary, 'bound_by' | 'units'> | null | undefined,
): Partial<Pick<ProposalSummary, 'bound_by' | 'units'>> {
  return side === null || side === undefined ? {} : { bound_by: side.bound_by, units: side.units };
}
